import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';

import {
  BusinessDocumentPipelineBundle,
  BusinessDocumentWorkspaceModel,
} from '../../../core/services/business-document.service';

@Component({
  selector: 'app-business-document-next-buttons',
  standalone: false,
  templateUrl: './business-document-next-buttons.html',
  styleUrl: './business-document-next-buttons.scss',
})
export class BusinessDocumentNextButtons {
  @Input() businessDocumentId = '';
  @Input() formId = '';
  @Input() formGroup: FormGroup | null = null;
  @Input() workspace: BusinessDocumentWorkspaceModel | null = null;
  @Input() creationPipeline: BusinessDocumentPipelineBundle | null = null;
  @Input() saved = false;
  @Input() draft = false;
  @Input() pending = false;
  @Input() canCreate = true;
  @Input() canSaveDraft = false;
  @Input() canDeleteDraft = false;
  @Input() accessMode: 'observe' | 'edit' = 'observe';
  @Input() createLabel = 'Save';
  @Output() nextStepClicked = new EventEmitter<string>();
  @Output() saveDraftClicked = new EventEmitter<void>();
  @Output() deleteDraftClicked = new EventEmitter<void>();
  @Output() createAtStepClicked = new EventEmitter<string>();

  errorMessage = '';

  get transitions(): BusinessDocumentWorkspaceModel['available_transitions'] {
    const currentStep = this.workspace?.pipeline.steps.find(
      (item) => item.step.id === this.workspace?.document.current_step_id,
    );
    if (
      !this.saved
      || this.draft
      || !this.businessDocumentId
      || !this.workspace?.can_edit_current_step
      || this.accessMode !== 'edit'
      || currentStep?.step.is_terminal
    ) {
      return [];
    }
    return [...this.workspace.available_transitions]
      .filter((transition) => transition.active)
      .sort((left, right) => left.sequence - right.sequence);
  }

  get creationSteps(): BusinessDocumentPipelineBundle['steps'] {
    const pipeline = this.workspace?.pipeline ?? this.creationPipeline;
    if (this.saved && !this.draft) return [];
    return [...(pipeline?.steps ?? [])]
      .filter((item) => item.step.active && item.step.is_initial)
      .sort((left, right) => left.step.sequence - right.step.sequence);
  }

  requestTransition(actionCode: string): void {
    if (this.pending || !this.businessDocumentId) return;
    this.formGroup?.markAllAsTouched();
    if (this.formGroup?.invalid) {
      this.errorMessage = 'Fix the form errors before moving this document to the next step.';
      return;
    }

    this.errorMessage = '';
    this.nextStepClicked.emit(actionCode);
  }

  requestSaveDraft(): void {
    if (this.pending || !this.canCreate) return;
    this.formGroup?.markAllAsTouched();
    if (this.formGroup?.invalid) {
      this.errorMessage = 'Fix the form errors before saving this draft.';
      return;
    }

    this.errorMessage = '';
    this.saveDraftClicked.emit();
  }

  requestCreateAtStep(stepId: string): void {
    if (this.pending || !this.canCreate) return;
    this.formGroup?.markAllAsTouched();
    if (this.formGroup?.invalid) {
      this.errorMessage = 'Fix the form errors before saving this document.';
      return;
    }

    this.errorMessage = '';
    this.createAtStepClicked.emit(stepId);
  }

  requestDeleteDraft(): void {
    if (this.pending || !this.saved || !this.draft || !this.canDeleteDraft) return;
    this.errorMessage = '';
    this.deleteDraftClicked.emit();
  }

}
